import type { IThemeStorage } from "../interfaces/IThemeStorage";
import type { Theme, Mode } from "../types/theme.types";
import {
	STORAGE_KEYS,
	DEFAULT_THEME,
	DEFAULT_MODE,
} from "../types/theme.types";
import { themeSchema, modeSchema } from "../schemas/theme.schema";

const isBrowser = typeof globalThis.window !== "undefined";

export class LocalStorageThemeStorage implements IThemeStorage {
	private isAvailable(): boolean {
		if (!isBrowser) return false;

		try {
			const test = "__storage_test__";
			globalThis.localStorage.setItem(test, test);
			globalThis.localStorage.removeItem(test);
			return true;
		} catch {
			return false;
		}
	}

	getTheme(): Theme {
		if (!this.isAvailable()) {
			return DEFAULT_THEME;
		}

		try {
			const stored = globalThis.localStorage.getItem(STORAGE_KEYS.THEME);
			if (!stored) {
				return DEFAULT_THEME;
			}

			const validated = themeSchema.parse(stored);
			return validated;
		} catch {
			this.setTheme(DEFAULT_THEME);
			return DEFAULT_THEME;
		}
	}

	setTheme(theme: Theme): void {
		if (!this.isAvailable()) {
			return;
		}

		try {
			const validated = themeSchema.parse(theme);
			globalThis.localStorage.setItem(STORAGE_KEYS.THEME, validated);
		} catch (error) {
			console.warn("Invalid theme value", error);
		}
	}

	getMode(): Mode {
		if (!this.isAvailable()) {
			return DEFAULT_MODE;
		}

		try {
			const stored = globalThis.localStorage.getItem(STORAGE_KEYS.MODE);
			if (!stored) {
				return DEFAULT_MODE;
			}

			const validated = modeSchema.parse(stored);
			return validated;
		} catch {
			this.setMode(DEFAULT_MODE);
			return DEFAULT_MODE;
		}
	}

	setMode(mode: Mode): void {
		if (!this.isAvailable()) {
			return;
		}

		try {
			const validated = modeSchema.parse(mode);
			globalThis.localStorage.setItem(STORAGE_KEYS.MODE, validated);
		} catch (error) {
			console.warn("Invalid mode value", error);
		}
	}

	clear(): void {
		if (!this.isAvailable()) {
			return;
		}

		try {
			globalThis.localStorage.removeItem(STORAGE_KEYS.THEME);
			globalThis.localStorage.removeItem(STORAGE_KEYS.MODE);
		} catch (error) {
			console.warn("Failed to clear theme storage", error);
		}
	}
}
